import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { 
  Briefcase, 
  Calendar, 
  FileCheck, 
  Video, 
  Upload, 
  Bell, 
  TrendingUp,
  CheckCircle,
  Clock,
  AlertCircle,
  ArrowRight,
  Loader2,
  ShieldCheck
} from 'lucide-react';
import { candidate, jobs, interviews as interviewsApi } from '../../api';

const CandidateDashboard = () => {
  const navigate = useNavigate();
  const userName = localStorage.getItem('userName') || 'User';
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState({
    profileCompletion: 0,
    jobMatches: 0,
    interviewsScheduled: 0,
    applicationsSubmitted: 0,
    aiScore: 0
  });
  const [recentMatches, setRecentMatches] = useState([]);
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [relocationChecklist, setRelocationChecklist] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userType = localStorage.getItem('userType');
    
    if (!token || userType !== 'candidate') {
      navigate('/login');
      return;
    }
    
    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [statsData, jobMatchesData, checklistData, notificationsData] = await Promise.all([
        candidate.getStats().catch(() => ({
          profile_completion: 50,
          job_matches: 0,
          interviews_scheduled: 0,
          applications_submitted: 0,
          ai_score: 0
        })),
        candidate.getJobMatches().catch(() => []),
        candidate.getRelocationChecklist().catch(() => ({ tasks: [] })),
        candidate.getNotifications().catch(() => [])
      ]);

      setUserStats({
        profileCompletion: statsData.profile_completion || 50,
        jobMatches: statsData.job_matches || jobMatchesData.length,
        interviewsScheduled: statsData.interviews_scheduled || 0,
        applicationsSubmitted: statsData.applications_submitted || 0,
        aiScore: statsData.ai_score || 0
      });

      // Format job matches for display
      const formattedMatches = jobMatchesData.slice(0, 3).map(job => ({
        id: job._id,
        title: job.title,
        hospital: job.employer_name || 'Healthcare Facility',
        location: job.location,
        salary: job.salary,
        match: job.match_score || 75
      }));
      setRecentMatches(formattedMatches);

      // Format relocation checklist
      const tasks = checklistData.tasks || [];
      setRelocationChecklist(tasks.map(task => ({
        id: task.id,
        task: task.task,
        status: task.status,
        category: task.category
      })));

      // Format notifications
      setNotifications(notificationsData.slice(0, 3).map(notif => ({
        id: notif._id,
        type: notif.type,
        title: notif.title,
        message: notif.message,
        date: new Date(notif.created_at).toLocaleDateString(),
        read: notif.read
      })));

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { icon: ShieldCheck, label: 'Get Verified', link: '/candidate/verification', color: 'bg-teal-500' },
    { icon: Video, label: 'Record Video', link: '/candidate/video-interview', color: 'bg-purple-500' },
    { icon: Briefcase, label: 'Browse Jobs', link: '/candidate/job-matches', color: 'bg-green-500' },
    { icon: Calendar, label: 'View Interviews', link: '/candidate/interviews', color: 'bg-orange-500' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#42b1c2] mx-auto mb-4" />
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Welcome back, {userName}!</h1>
            <p className="text-gray-600">Here&apos;s your recruitment journey progress</p>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Profile Completion</p>
                    <p className="text-3xl font-bold text-gray-900">{userStats.profileCompletion}%</p>
                  </div>
                  <div className="w-12 h-12 bg-[#42b1c2]/10 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-[#42b1c2]" />
                  </div>
                </div>
                <Progress value={userStats.profileCompletion} className="mt-3" />
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Job Matches</p>
                    <p className="text-3xl font-bold text-gray-900">{userStats.jobMatches}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Interviews</p>
                    <p className="text-3xl font-bold text-gray-900">{userStats.interviewsScheduled}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Applications</p>
                    <p className="text-3xl font-bold text-gray-900">{userStats.applicationsSubmitted}</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <FileCheck className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">AI Score</p>
                    <p className="text-3xl font-bold text-gray-900">{userStats.aiScore}</p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-yellow-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickActions.map((action, index) => (
                <Link key={index} to={action.link}>
                  <Card className="hover:shadow-lg transition-all hover:scale-105 cursor-pointer">
                    <CardContent className="p-6 text-center">
                      <div className={`w-14 h-14 ${action.color} rounded-lg flex items-center justify-center mx-auto mb-3`}>
                        <action.icon className="h-7 w-7 text-white" />
                      </div>
                      <p className="font-semibold text-gray-900">{action.label}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Recent Job Matches */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Recent Job Matches</CardTitle>
                  <Link to="/candidate/job-matches">
                    <Button variant="ghost" size="sm">
                      View All
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recentMatches.length > 0 ? (
                      recentMatches.map((job) => (
                        <div key={job.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-[#42b1c2] transition-colors">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-semibold text-gray-900">{job.title}</h3>
                              <p className="text-sm text-gray-600">{job.hospital}</p>
                            </div>
                            <Badge className="bg-[#42b1c2] hover:bg-[#3a9fb0]">{job.match}% Match</Badge>
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <span>{job.location}</span>
                            <span>•</span>
                            <span>{job.salary}</span>
                          </div>
                          <Link to={`/candidate/job-matches/${job.id}`}>
                            <Button size="sm" className="mt-3 bg-[#42b1c2] hover:bg-[#3a9fb0] text-white">
                              View Details
                            </Button>
                          </Link>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No job matches yet</p>
                        <p className="text-sm text-gray-400 mt-1">Complete your profile to see matching jobs</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Upcoming Interviews */}
              <Card className="mt-8">
                <CardHeader>
                  <CardTitle>Upcoming Interviews</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {upcomingInterviews.map((interview) => (
                      <div key={interview.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-[#42b1c2] transition-colors">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="font-semibold text-gray-900">{interview.position}</h3>
                            <p className="text-sm text-gray-600">{interview.hospital}</p>
                          </div>
                          <Badge variant="outline">{interview.type}</Badge>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Calendar className="h-4 w-4" />
                          <span>{interview.date} at {interview.time}</span>
                        </div>
                        <Button size="sm" className="mt-3" variant="outline">
                          View Details
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Relocation Checklist */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Relocation Progress</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {relocationChecklist.length > 0 ? (
                      relocationChecklist.slice(0, 5).map((item) => (
                        <div key={item.id} className="flex items-center space-x-3">
                          {item.status === 'completed' ? (
                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                          ) : item.status === 'in-progress' ? (
                            <Clock className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                          ) : (
                            <AlertCircle className="h-5 w-5 text-gray-400 flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{item.task}</p>
                            <p className="text-xs text-gray-500">{item.category}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No checklist items yet</p>
                    )}
                  </div>
                  <Link to="/candidate/relocation">
                    <Button variant="ghost" size="sm" className="w-full mt-4">
                      View Full Checklist
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Notifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Bell className="h-5 w-5 mr-2" />
                    Recent Notifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {notifications.length > 0 ? (
                      notifications.map((notif) => (
                        <div key={notif.id} className={`p-3 rounded-lg ${notif.read ? 'bg-gray-50' : 'bg-[#42b1c2]/10'}`}>
                          <p className="text-sm font-medium text-gray-900">{notif.title}</p>
                          <p className="text-xs text-gray-600 mt-1">{notif.message}</p>
                          <p className="text-xs text-gray-500 mt-2">{notif.date}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No notifications yet</p>
                    )}
                  </div>
                  <Link to="/candidate/notifications">
                    <Button variant="ghost" size="sm" className="w-full mt-4">
                      View All Notifications
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CandidateDashboard;